import { IAppRoute, IAppRouteGroup, routes } from '@app/routing/routes';
import {
  Button,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadMain,
  MastheadToggle,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,
  SkipToContent,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem
} from '@patternfly/react-core';
import { BarsIcon } from '@patternfly/react-icons';
import * as React from 'react';
import { NavLink, useLocation } from 'react-router';

import nuadaLogo from '@project/assets/bgimages/nuada-logo-name.png';

import { SessionWarningModal } from '@app/components/SessionWarningModal';
import { useSessionTimeout } from '@app/hooks/useSessionTimeout';
import LanguageSwitcher from '@app/i18n/LanguageSwitcher';
import UserDropdown from '@app/layout/UserDropdown/UserDropdown';
import { useKeycloak } from '@project/auth/keycloakInstance';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EventsHost } from '@app/Events/EventsHost';


// Helper function to safely translate route labels
const translateRouteLabel = (label: string | undefined, defaultLabel: string | undefined, t: (key: string, defaultValue: string) => string): string => {
  if (!label) return defaultLabel ?? '';
  return t(label, defaultLabel ?? label);
};

// Helper function to safely translate route titles
const translateRouteTitle = (title: string | undefined, defaultTitle: string | undefined, t: (key: string, defaultValue: string) => string): string => {
  if (!title) return defaultTitle ?? '';
  return t(title, defaultTitle ?? title);
};

interface IAppLayout {
  children: React.ReactNode;
}

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const { showWarning, timeLeft } = useSessionTimeout(60);
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  // Update document title based on current route
  const location = useLocation();

  React.useEffect(() => {
    const currentRoute = routes.find(route => {
      if ('routes' in route) {
        return route.routes?.some(r => r.path === location.pathname);
      }
      return route.path === location.pathname;
    });

    if (currentRoute) {
      const title = translateRouteTitle(currentRoute.title, currentRoute.defaultTitle, t);
      document.title = title;
    }
  }, [location.pathname, t]);

  useEffect(() => {
    const themeChannel = new BroadcastChannel('theme_channel');

    themeChannel.onmessage = (event) => {
      const isDark = event.data;
      const theme = isDark ? 'dark' : 'light';

      if (isDark) {
        document.documentElement.classList.add('pf-v6-theme-dark');
      } else {
        document.documentElement.classList.remove('pf-v6-theme-dark');
      }

      localStorage.setItem('theme', theme);

      globalThis.dispatchEvent(new CustomEvent('local-theme-sync', { detail: isDark }));
    };

    return () => themeChannel.close();
  }, []);

  // Header bar
  const masthead = (
    <Masthead>
      {/* Logo and menu drop down  */}
      <MastheadMain>
        <MastheadToggle>
          <Button
            icon={<BarsIcon color="white" />}
            variant="plain"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={t('appLayout.globalNavigation', 'Global navigation')}
          />
        </MastheadToggle>
        <MastheadBrand>
          <img id="nuada-icon" src={nuadaLogo} alt={t('appLayout.nuadaLogo', 'NUADA Logo')}></img>
        </MastheadBrand>
      </MastheadMain>

      <MastheadContent>
        <Toolbar id="masthead-toolbar" isFullHeight>
          <ToolbarContent> { }
            <ToolbarGroup align={{ default: 'alignEnd' }}> { }
              <ToolbarItem align={{ default: 'alignEnd' }} visibility={{ default: 'hidden', md: 'visible' }}>
                <LanguageSwitcher /> { }
              </ToolbarItem>
              <ToolbarItem variant="separator" visibility={{ default: 'hidden', md: 'visible' }} />
              <ToolbarItem visibility={{ default: 'hidden', md: 'visible' }}>
                <UserDropdown />
              </ToolbarItem>
            </ToolbarGroup>
          </ToolbarContent>
        </Toolbar>
      </MastheadContent>
    </Masthead>
  );

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavItem key={`${route.label}-${index}`} id={`${route.label}-${index}`} isActive={route.path === location.pathname}>
      <NavLink to={route.path}>{translateRouteLabel(route.label, route.defaultLabel, t)}</NavLink>
    </NavItem>
  );

  const hasRequiredRole = (requiredRoles?: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    return requiredRoles.some((role) => keycloak.hasRealmRole(role) || keycloak.hasResourceRole(role));
  };

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => {
    if (!hasRequiredRole(group.requiredRoles)) {
      return null;
    }

    const visibleRoutes = group.routes.filter(canAccessRoute);

    if (visibleRoutes.length === 0) {
      return null;
    }

    return (
      <NavExpandable
        key={`${group.label}-${groupIndex}`}
        id={`${group.label}-${groupIndex}`}
        title={translateRouteLabel(group.label, group.defaultLabel, t)}
        isActive={visibleRoutes.some((route) => route.path === location.pathname)}
      >
        {visibleRoutes.map((route, idx) => route.label && renderNavItem(route, idx))}
      </NavExpandable>
    );
  };

  const canAccessRoute = (route: IAppRoute): boolean => {
    return hasRequiredRole(route.requiredRoles);
  };

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {routes.map((route, idx) => {
          if (!route.label) {
            return null;
          }

          if (route.routes) {
            return renderNavGroup(route as IAppRouteGroup, idx);
          }

          if (!canAccessRoute(route as IAppRoute)) {
            return null;
          }

          return renderNavItem(route as IAppRoute, idx);
        })}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar>
      <PageSidebarBody>{Navigation}</PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer?.focus();
      }}
      href={`#${pageId}`}
    >
      {t('appLayout.skipToContent', 'Skip to Content')}
    </SkipToContent>
  );

  return (
    <>
      <Page
        isContentFilled
        mainContainerId={pageId}
        masthead={masthead}
        sidebar={sidebarOpen && Sidebar}
        skipToContent={PageSkipToContent}

        style={{
          ['--pf-v6-c-page__main-container--MaxHeight' as any]: '100%',
          ['--pf-v6-c-page--inset' as any]: '0',
          ['--pf-t--global--spacer--inset--page-chrome' as any]: '0',
          ['--pf-v6-c-page__main-container--BorderRadius' as any]: '0'
        }}
      >
        {/* This wrapper is the main content area */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Always-mounted iframe container */}
          <div
            id="events-iframe-container"
            style={{
              position: 'absolute',
              inset: 0,
              border: 'none',
              margin: 0,
              padding: 0,
              display: 'none', // hidden by default
              pointerEvents: 'none', // disabled by default
            }}
          >
            {/* Render the iframe into this container */}
            <EventsHost />
          </div>

          {/* Normal page content */}
          {children}
        </div>
      </Page>

      <SessionWarningModal isOpen={showWarning} timeLeft={timeLeft} />
    </>
  );
};

export { AppLayout };
